import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoader } from "@/components/common/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Plus, Flag } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { SourcesManager } from "@/components/admin/SourcesManager";

export default function StatusesSources() {
  const { isAdmin } = useAuth();
  return (
    <div>
      <PageHeader title="Statuses & Sources" description="Configure recruiting opportunity statuses and candidate sources." />
      <div className={`grid gap-6 ${isAdmin ? "lg:grid-cols-2" : ""}`}>
        {isAdmin && <ConfigColumn kind="statuses" title="Universal Opportunity Statuses" icon={Flag}
          placeholder="e.g. Nurturing" testKey="status" />}
        <SourcesManager />
      </div>
    </div>
  );
}

function ConfigColumn({ kind, title, icon: Icon, placeholder, testKey }) {
  const [items, setItems] = useState(null);
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/${kind}`); setItems(data.items); }
    catch (e) { toast.error(apiError(e)); }
  }, [kind]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!label.trim()) return;
    setSaving(true);
    try { await api.post(`/${kind}`, { label: label.trim() }); setLabel(""); load(); toast.success(`${title.slice(0, -1)} added`); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const toggle = async (it) => {
    try { await api.patch(`/${kind}/${it.id}`, { active: !it.active }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!items) return <PageLoader />;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid={`${testKey}-config`}>
      <div className="flex items-center gap-2 mb-4">
        <span className="w-8 h-8 rounded-lg bg-brand/10 flex items-center justify-center"><Icon className="w-4 h-4 text-brand" /></span>
        <h3 className="font-display font-semibold text-slate-900 text-sm">{title}</h3>
      </div>
      <div className="flex gap-2 mb-4">
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={placeholder}
          onKeyDown={(e) => e.key === "Enter" && add()} data-testid={`${testKey}-input`} />
        <Button onClick={add} disabled={saving} className="bg-brand hover:bg-brand-strong shrink-0" data-testid={`${testKey}-add`}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <ul className="space-y-1.5">
        {items.map((it) => (
          <li key={it.id} className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2" data-testid={`${testKey}-item-${it.id}`}>
            <div className="flex items-center gap-2">
              <span className={`text-sm ${it.active ? "text-slate-700" : "text-slate-400 line-through"}`}>{it.label}</span>
              {it.is_system && <span className="text-[10px] uppercase tracking-wide text-slate-400 bg-slate-100 rounded px-1.5 py-0.5">system</span>}
            </div>
            <Switch checked={!!it.active} onCheckedChange={() => toggle(it)} data-testid={`${testKey}-toggle-${it.id}`} />
          </li>
        ))}
      </ul>
    </div>
  );
}
